import { useMemo } from 'react';
import {
  addDays,
  aggregatePeriods,
  partitionCarriedForwardByDirection,
  resolveEntry,
  type Basis,
  type CarriedForwardGroup,
  type CarriedForwardPartition,
  type CashFlowEntry as CoreEntry,
  type DayRollup as CoreDayRollup,
  type PeriodRollup,
  type Priority,
  type ResolvedEntry,
} from '@progear/finance-core';
import type { CashFlowEntry, DayRollup, MoneyMode } from '@/lib/cashFlow';

/**
 * Pure model layer for the cash-flow calendar (module 1.1). It maps the rolled-up
 * window the API returns (`days` + `entries`, both finance-core truth) into the
 * lookup tables the four granularity layouts render from — and computes NOTHING
 * monetary itself (L9): per-period sums come from `aggregatePeriods`, the running
 * balance from each day's `runningBalance`, and an entry's effective placement
 * from `resolveEntry`. This keeps every number identical to the table lens.
 */

/** An entry paired with its finance-core resolution (effective date / certainty). */
export interface PlacedEntry {
  e: CashFlowEntry;
  r: ResolvedEntry;
}

/** A floating-hover anchor: which entry, and the cursor position it opened at. */
export interface HoverPos {
  id: string;
  x: number;
  y: number;
}

/** Estonian weekday initials, Monday-first (the team reads weeks Mon→Sun). */
export const WEEKDAYS = ['E', 'T', 'K', 'N', 'R', 'L', 'P'] as const;

/** Estonian month names (nominative, capitalised) indexed 0=Jaanuar … 11=Detsember. */
export const MONTHS = [
  'Jaanuar',
  'Veebruar',
  'Märts',
  'Aprill',
  'Mai',
  'Juuni',
  'Juuli',
  'August',
  'September',
  'Oktoober',
  'November',
  'Detsember',
] as const;

/** Roman quarter numerals (the Estonian convention, e.g. "II kvartal"). */
export const ROMAN = ['I', 'II', 'III', 'IV'] as const;

/** One-letter badge for the active money mode (Neto / Bruto). */
export const MODE_BADGE: Record<MoneyMode, string> = { net: 'N', gross: 'B' };

/** Accent classes per money mode so the N / B badge reads clearly apart. */
export function modeBadgeCls(m: MoneyMode): string {
  return m === 'net'
    ? 'border-[#2dd4bf]/45 bg-[#2dd4bf]/12 text-[#2dd4bf]'
    : 'border-[hsl(var(--vatmode))]/45 bg-[hsl(var(--vatmode))]/12 text-[hsl(var(--vatmode))]';
}

/**
 * Estonian UI label for a document type (codes stay English — replit.md).
 * Single source: re-exported from DOCTYPE_FACET in cashFlowFilters so the
 * calendar, register and faceted filter never drift on labels (master rule:
 * one truth per label — a new doc type is added once, in the facet).
 */
export { DOC_TYPE_LABEL } from '@/lib/cashFlowFilters';

/** Certainty glyph + Estonian label (kindlusaste) — reused by the doc hover. */
export const CERTAINTY_LABEL: Record<CashFlowEntry['certainty'], string> = {
  actual: 'Kindel',
  committed: 'Kokkulepitud',
  forecast: 'Prognoos',
};
export const CERTAINTY_GLYPH: Record<CashFlowEntry['certainty'], string> = {
  actual: '●',
  committed: '◐',
  forecast: '○',
};

export const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Compose an ISO calendar day from year / zero-based month / day-of-month. */
export const isoDay = (y: number, m0: number, d: number): string => `${y}-${pad2(m0 + 1)}-${pad2(d)}`;

/** ISO day → "D. Kuu" (e.g. "26. Juuni"); display only. */
export function fmtDayMonth(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${d}. ${MONTHS[m - 1]}`;
}

/** UTC weekday for an ISO day: 0 = Sunday … 6 = Saturday (DST-safe). */
function dow(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay();
}

/** Calendar days in a month (UTC): day 0 of the next month. */
function daysInMonth(y: number, m0: number): number {
  return new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
}

/** Every {year, zero-based month} the window touches, first → last inclusive. */
export function monthsBetween(first: string, last: string): { y: number; m0: number }[] {
  const out: { y: number; m0: number }[] = [];
  let y = Number(first.slice(0, 4));
  let m0 = Number(first.slice(5, 7)) - 1;
  const lastY = Number(last.slice(0, 4));
  const lastM0 = Number(last.slice(5, 7)) - 1;
  while (y < lastY || (y === lastY && m0 <= lastM0)) {
    out.push({ y, m0 });
    if (++m0 > 11) {
      m0 = 0;
      y += 1;
    }
  }
  return out;
}

/** A Monday-first month grid: leading nulls pad to the first weekday, then 1..N. */
export function buildCells(y: number, m0: number): (number | null)[] {
  const lead = (new Date(Date.UTC(y, m0, 1)).getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  const cells: (number | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= daysInMonth(y, m0); d++) cells.push(d);
  return cells;
}

/** Monday-first ISO weeks (each a 7-ISO-date row) covering [first, last] inclusive. */
export function weeksBetween(first: string, last: string): string[][] {
  let monday = addDays(first, -((dow(first) + 6) % 7));
  const weeks: string[][] = [];
  while (monday <= last) {
    const week: string[] = [];
    for (let i = 0; i < 7; i++) week.push(addDays(monday, i));
    weeks.push(week);
    monday = addDays(monday, 7);
  }
  return weeks;
}

/** A quarter grouping of the window's months (for the Kvartal layout). */
export interface QuarterGroup {
  y: number;
  /** Zero-based quarter index, 0 = I … 3 = IV. */
  q: number;
  months: { y: number; m0: number }[];
}

/** Group the window's months into their quarters, preserving chronological order. */
export function quartersBetween(months: { y: number; m0: number }[]): QuarterGroup[] {
  const out: QuarterGroup[] = [];
  for (const m of months) {
    const q = Math.floor(m.m0 / 3);
    let group = out.find((g) => g.y === m.y && g.q === q);
    if (!group) {
      group = { y: m.y, q, months: [] };
      out.push(group);
    }
    group.months.push(m);
  }
  return out;
}

/** The lookup tables every layout reads — all numbers are finance-core truth. */
export interface CalendarModel {
  /** Per-day rollup, keyed by ISO day. */
  rollupByDate: Map<string, DayRollup>;
  /** Opening balance for a day = the previous day's running balance. */
  openingByDate: Map<string, number>;
  /** Entries placed on their finance-core effective date (carry-forward aware). */
  entriesByDate: Map<string, PlacedEntry[]>;
  /** Months the window spans, chronological. */
  months: { y: number; m0: number }[];
  /** Per-month aggregate, keyed `YYYY-MM`. */
  monthRollups: Map<string, PeriodRollup>;
  /** Per-quarter aggregate, keyed `YYYY-Qn`. */
  quarterRollups: Map<string, PeriodRollup>;
  firstDate: string;
  lastDate: string;
  /** Window closing balance (the last day's running balance). */
  closingBalance: number;
  /** The confirmed-only twin of `closingBalance` (the last day's
   *  `confirmedRunningBalance` — no forecast money; finance-core). */
  confirmedClosingBalance: number;
  /** Overdue, unprioritised documents folded into ONE summary group per direction
   *  (finance-core; the calendar collapses them onto today's cell — F1 / L14). A
   *  prioritised carried-forward document is excluded, so it stays a chip (F2). */
  carriedForward: CarriedForwardPartition;
}

/**
 * Build the calendar model from a rolled-up window. Returns null for an empty
 * window so the grid can render its empty state. Memoised on its inputs.
 */
export function useCalendarModel(
  days: DayRollup[],
  entries: CashFlowEntry[],
  today: string,
  openingBalance: number,
  basis: Basis = 'cashBasis',
): CalendarModel | null {
  return useMemo(() => {
    if (days.length === 0) return null;

    const rollupByDate = new Map<string, DayRollup>(days.map((d) => [d.date, d]));

    const openingByDate = new Map<string, number>();
    let prev = openingBalance;
    for (const d of days) {
      openingByDate.set(d.date, prev);
      prev = d.runningBalance;
    }

    const entriesByDate = new Map<string, PlacedEntry[]>();
    for (const e of entries) {
      const r = resolveEntry(e as CoreEntry, today, basis);
      const list = entriesByDate.get(r.effectiveDate) ?? [];
      list.push({ e, r });
      entriesByDate.set(r.effectiveDate, list);
    }

    const coreDays = days as unknown as CoreDayRollup[];
    const monthRollups = new Map<string, PeriodRollup>();
    for (const p of aggregatePeriods(coreDays, 'month')) monthRollups.set(p.key, p);
    const quarterRollups = new Map<string, PeriodRollup>();
    for (const p of aggregatePeriods(coreDays, 'quarter')) quarterRollups.set(p.key, p);

    // Under the accrual basis this partition is structurally empty (nothing is
    // ever carried), so the fold-to-today summary groups disappear on their own.
    const carriedForward = partitionCarriedForwardByDirection(
      entries as unknown as (CoreEntry & { priority: Priority | null })[],
      today,
      basis,
    );

    return {
      rollupByDate,
      openingByDate,
      entriesByDate,
      months: monthsBetween(days[0].date, days[days.length - 1].date),
      monthRollups,
      quarterRollups,
      firstDate: days[0].date,
      lastDate: days[days.length - 1].date,
      closingBalance: days[days.length - 1].runningBalance,
      confirmedClosingBalance: days[days.length - 1].confirmedRunningBalance,
      carriedForward,
    };
  }, [days, entries, today, openingBalance, basis]);
}

/**
 * Split ONE day's arranged entries into the two carried-forward summary groups
 * (income + cost) the calendar collapses overdue, unprioritised documents into,
 * plus the chips that stay visible. The folded ids come from finance-core's
 * {@link CarriedForwardPartition} — a prioritised carried-forward document is NOT
 * in it, so it stays a chip (F2). Pure: it only filters by id and never sums (the
 * group totals are finance-core's; L9). Applied to today's cell only.
 */
export function splitCarriedForward(
  placed: PlacedEntry[],
  partition: CarriedForwardPartition,
): { incomeGroup: CarriedForwardGroup | null; costGroup: CarriedForwardGroup | null; chips: PlacedEntry[] } {
  const foldIds = new Set<string>([...partition.income.ids, ...partition.cost.ids]);
  return {
    incomeGroup: partition.income.ids.length > 0 ? partition.income : null,
    costGroup: partition.cost.ids.length > 0 ? partition.cost : null,
    chips: placed.filter((p) => !foldIds.has(p.e.id)),
  };
}
